import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaPlay, FaPause, FaClock } from 'react-icons/fa';
import { liveEventApi } from '../../api/scorecard';
import styles from './GameTimer.module.css';

const GameTimer = ({ 
  eventId, 
  isEventCompleted, 
  onTimerUpdate, 
  compact = false, 
  refreshKey = 0,
  // Game state props to control timer behavior
  hasActivePeriod = true,
  allPeriodsComplete = false,
  gameDecided = false,
}) => {
  const [timerData, setTimerData] = useState(null);
  const [displayTime, setDisplayTime] = useState('--:--');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Add immediate UI state for instant button feedback
  const [isRunningUI, setIsRunningUI] = useState(false);
  
  // Refs for client-side counting
  const localSecondsRef = useRef(0);
  const backendSecondsRef = useRef(0);
  const isRunningRef = useRef(false);
  const lastSyncTimeRef = useRef(null);
  const lastMinuteRef = useRef(-1); // Track last minute to prevent unnecessary parent updates
  const lastRefreshKeyRef = useRef(refreshKey);

  // Format seconds to MM:SS (define BEFORE fetchTimer)
  const formatTime = useCallback((totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, []);

  // Update display time (define BEFORE fetchTimer)
  const updateDisplay = useCallback((seconds) => {
    setDisplayTime(formatTime(seconds));
  }, [formatTime]);

  // Fetch timer state from backend (now formatTime & updateDisplay are available)
  const fetchTimer = useCallback(async () => {
    try {
      const response = await liveEventApi.getTimer(eventId);
      console.log('⏱️ Timer synced from backend:', response.timer);
      
      if (response.timer) {
        setTimerData(response.timer);
        
        // Update refs with backend data
        const newSeconds = response.timer.elapsedSeconds || 0;
        const newIsRunning = response.timer.isRunning || false;
        
        backendSecondsRef.current = newSeconds;
        localSecondsRef.current = newSeconds;
        isRunningRef.current = newIsRunning;
        lastSyncTimeRef.current = Date.now();
        lastMinuteRef.current = Math.floor(newSeconds / 60); // Reset minute tracking
        
        console.log('✅ Timer refs updated:', {
          backendSeconds: newSeconds,
          localSeconds: newSeconds,
          isRunning: newIsRunning,
          displayTime: formatTime(newSeconds)
        });
        
        // Update UI state
        setIsRunningUI(newIsRunning);
        
        // Update display
        updateDisplay(newSeconds);
        
        if (onTimerUpdate) {
          onTimerUpdate(response.timer);
        }
      } else {
        console.warn('⚠️ No timer data in response');
        setTimerData(null);
      }
    } catch (err) {
      // Don't log "Event not live" errors - these are expected before event starts
      if (!err.message?.includes('Event not live') && !err.message?.includes('not Live') && !err.message?.includes('No active period')) {
        console.warn('⏱️ Timer fetch error:', err.message);
      } else {
        console.log('⏸️ Timer not available:', err.message);
      }
      setTimerData(null);
    }
  }, [eventId, onTimerUpdate, formatTime, updateDisplay]);

  // Client-side timer tick (runs every 100ms for smooth updates)
  useEffect(() => {
    const clientInterval = setInterval(() => {
      if (isRunningRef.current && lastSyncTimeRef.current) {
        // Calculate elapsed time since last sync
        const timeSinceSync = Math.floor((Date.now() - lastSyncTimeRef.current) / 1000);
        const currentSeconds = backendSecondsRef.current + timeSinceSync;
        
        // Debug log every 10 seconds
        if (currentSeconds % 10 === 0 && localSecondsRef.current !== currentSeconds) {
          console.log('⏱️ Client timer tick:', {
            backendSeconds: backendSecondsRef.current,
            timeSinceSync,
            currentSeconds,
            displayTime: formatTime(currentSeconds)
          });
        }
        
        // Update local display
        localSecondsRef.current = currentSeconds;
        updateDisplay(currentSeconds);
        
        // Update parent component ONLY when minute changes (to prevent excessive re-renders)
        const currentMinute = Math.floor(currentSeconds / 60);
        if (onTimerUpdate && timerData && currentMinute !== lastMinuteRef.current) {
          lastMinuteRef.current = currentMinute;
          onTimerUpdate({
            ...timerData,
            elapsedSeconds: currentSeconds,
            displayTime: formatTime(currentSeconds),
            displayMinutes: currentMinute
          });
          console.log('⏱️ Timer minute changed, notifying parent:', currentMinute);
        }
      }
    }, 100); // Update display every 100ms for smooth animation

    return () => clearInterval(clientInterval);
  }, [timerData, onTimerUpdate, updateDisplay, formatTime]);

  // Backend sync (fetch periodically)
  useEffect(() => {
    console.log('GameTimer mounted for event:', eventId);
    
    // Initial fetch
    fetchTimer();

    // Sync with backend every 30 seconds (not every 1 second!)
    const syncInterval = setInterval(() => {
      console.log('Periodic backend sync...');
      fetchTimer();
    }, 30000); // 30 seconds

    // Also sync when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Tab visible - syncing timer...');
        fetchTimer();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      console.log('GameTimer unmounting');
      clearInterval(syncInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [eventId, fetchTimer]);

  // CRITICAL: Re-fetch timer when refreshKey changes (e.g., after period start/end)
  useEffect(() => {
    if (refreshKey !== lastRefreshKeyRef.current) {
      console.log('⏱️ Refresh key changed, re-syncing timer...', { 
        oldKey: lastRefreshKeyRef.current, 
        newKey: refreshKey 
      });
      lastRefreshKeyRef.current = refreshKey;
      fetchTimer();
    }
  }, [refreshKey, fetchTimer]);

  // Handle start/resume
  const handleStart = async () => {
    // Don't allow starting if game is over
    if (allPeriodsComplete && gameDecided) {
      console.log('⏱️ Timer start blocked: Game is complete');
      return;
    }
    
    // Don't allow starting if no active period (and all periods are complete)
    if (!hasActivePeriod && allPeriodsComplete) {
      console.log('⏱️ Timer start blocked: All periods complete, no active period');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Immediately update UI state for instant button feedback
      setIsRunningUI(true);
      
      // Send start request to backend
      await liveEventApi.startTimer(eventId);
      
      // IMPORTANT: Fetch from backend to get correct elapsed time
      await fetchTimer();
      
    } catch (err) {
      const errorMsg = err.message?.toLowerCase() || '';
      
      // Handle "Timer already running" error gracefully by syncing state
      if (errorMsg.includes('already running')) {
        console.log('Timer already running - syncing state from backend');
        await fetchTimer();
        return;
      }
      
      // Handle "No active period" - don't show error, just revert UI
      if (errorMsg.includes('no active period') || errorMsg.includes('without an active period')) {
        console.log('⏱️ No active period - timer cannot start');
        isRunningRef.current = false;
        setIsRunningUI(false);
        // Don't show error to user - the period controls will handle this
        return;
      }
      
      console.error('Error starting timer:', err);
      setError(err.message || 'Failed to start timer');
      // Revert on error
      isRunningRef.current = false;
      setIsRunningUI(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle pause
  const handlePause = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Immediately update local state for instant UI response
      isRunningRef.current = false;
      const currentSeconds = localSecondsRef.current;
      backendSecondsRef.current = currentSeconds;
      setIsRunningUI(false); // Instant UI update
      setTimerData(prev => prev ? { 
        ...prev, 
        isRunning: false,
        elapsedSeconds: currentSeconds 
      } : prev);
      
      // Then sync with backend
      await liveEventApi.pauseTimer(eventId, 'manual');
      await fetchTimer();
    } catch (err) {
      const errorMsg = err.message?.toLowerCase() || '';
      
      // Handle "Timer not running" gracefully - just sync state
      if (errorMsg.includes('not running') || errorMsg.includes('not currently running') || errorMsg.includes('already paused')) {
        console.log('⏱️ Timer already paused/stopped - syncing state');
        // Timer is already paused - keep the UI state and sync
        await fetchTimer();
        return;
      }
      
      console.error('Error pausing timer:', err);
      setError(err.message || 'Failed to pause timer');
      // Revert on error
      isRunningRef.current = true;
      setIsRunningUI(true);
      setTimerData(prev => prev ? { ...prev, isRunning: true } : prev);
    } finally {
      setLoading(false);
    }
  };

  // If no timer data yet, show loading state
  if (!timerData) {
    if (compact) {
      return (
        <div className={styles.compactTimer}>
          <span className={styles.compactLabel}>Timer:</span>
          <span className={styles.compactTime}>{displayTime}</span>
          <span className={styles.compactStatus}>
            {isEventCompleted ? '⏹️ Ended' : '⏸️ Not started'}
          </span>
        </div>
      );
    }
    return (
      <div className={styles.timerContainer}>
        <div className={styles.timerDisplay}>
          <FaClock className={styles.clockIcon} />
          <div className={styles.timeText}>{displayTime}</div>
        </div>
        <div className={styles.timerInfo}>
          <span style={{fontSize: '12px', color: 'rgba(255,255,255,0.5)'}}>
            {isEventCompleted ? '⏹️ Event completed' : '⏸️ Timer not started - Start a period first'}
          </span>
        </div>
      </div>
    );
  }

  const isRunning = isRunningUI; // Use UI state for instant feedback
  const canControl = timerData.canControl && !isEventCompleted;

  // Compact mode for header
  if (compact) {
    return (
      <div className={styles.compactTimer}>
        <span className={styles.compactLabel}>Timer:</span>
        <span className={styles.compactTime}>{displayTime}</span>
        {canControl && (
          <>
            <button
              onClick={isRunning ? handlePause : handleStart}
              disabled={loading}
              className={styles.compactButton}
              title={isRunning ? 'Pause Timer' : 'Start Timer'}
            >
              {isRunning ? <FaPause /> : <FaPlay />}
              <span>{isRunning ? 'Pause' : 'Start'}</span>
            </button>
          </>
        )}
        {timerData.pauseCount > 0 && (
          <span className={styles.compactPauseCount}>
            Paused {timerData.pauseCount}×
          </span>
        )}
      </div>
    );
  }

  // Full mode (original)
  return (
    <div className={styles.timerContainer}>
      <div className={styles.timerDisplay}>
        <FaClock className={styles.clockIcon} />
        <div className={styles.timeText}>
          {displayTime}
        </div>
        {isRunning && (
          <div className={styles.runningIndicator}>
            <div className={styles.pulse}></div>
          </div>
        )}
      </div>

      {canControl && (
        <div className={styles.timerControls}>
          {isRunning ? (
            <button
              onClick={handlePause}
              disabled={loading}
              className={`${styles.timerButton} ${styles.pauseButton}`}
              title="Pause Timer"
            >
              <FaPause />
              <span>Pause</span>
            </button>
          ) : (
            <button
              onClick={handleStart}
              disabled={loading || (allPeriodsComplete && !hasActivePeriod) || (allPeriodsComplete && gameDecided)}
              className={`${styles.timerButton} ${styles.startButton} ${
                ((allPeriodsComplete && !hasActivePeriod) || (allPeriodsComplete && gameDecided)) ? styles.disabledButton : ''
              }`}
              title={
                (allPeriodsComplete && gameDecided) ? 'Game is complete' :
                (allPeriodsComplete && !hasActivePeriod) ? 'All periods complete' :
                'Start Timer'
              }
            >
              <FaPlay />
              <span>Start</span>
            </button>
          )}
        </div>
      )}

      {error && (
        <div className={styles.timerError}>
          {error}
        </div>
      )}

      <div className={styles.timerInfo}>
        {timerData.pauseCount > 0 && (
          <span className={styles.pauseInfo}>
            Paused {timerData.pauseCount} time{timerData.pauseCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
};

export default GameTimer;

